import { AxiosResponse } from 'axios';
import HttpClientWithRetry from '../http/client/HttpClientWithRetry';
import {
  CertnFormData,
  CertnSecretsResponse,
  SuccessMessageResponse,
  UploadLogoResponse,
  WebApplyUrl,
} from '../modules/administration/certn/CertnType';

import { BackgroundChecks } from '../firebase/firestore/documents/candidate';
import {
  ModernHireConfigData,
  ModernHireUpdateResponseType,
} from '../modules/administration/ModernHireConfigView/ModernHireConfigData';
import { TemplateSettingResponse } from '../modules/administration/hireForm/templateSettings/templateSettings.interface';
import { I9FormData } from '../modules/administration/i9/I9Types';
import { OfferConditionsType } from '../firebase/firestore/documents/candidateAudit';
import {
  QuestionnaireTemplateResponse,
  QuestionTemplatePagination,
  SortDirection,
  SortField,
} from '../modules/administration/questionnaireTemplate/questionnaireTemplate.types';

export class AccountController {
  constructor(private readonly httpClient: HttpClientWithRetry) {}

  async getFavoriteSeekersList(accountId: string, userId: string): Promise<AxiosResponse> {
    const path = `/api/v1/account/${accountId}/user/${userId}/favourites`;
    return this.httpClient.get(path);
  }

  async saveNote(accountId: string, positionId: string, seekerId: string, note: string): Promise<void> {
    const path = `/api/v1/accounts/${accountId}/positions/${positionId}/seekers/${seekerId}/notes`;
    const body = { note };
    await this.httpClient.post(path, body);
  }

  async updateNote(accountId: string, positionId: string, seekerId: string, noteIndex: number, note?: string): Promise<void> {
    const path = `/api/v1/accounts/${accountId}/positions/${positionId}/seekers/${seekerId}/notes/${noteIndex}`;
    const body = { note };
    await this.httpClient.put(path, body);
  }

  async deleteNote(accountId: string, positionId: string, seekerId: string, noteIndex: number): Promise<void> {
    const path = `/api/v1/accounts/${accountId}/positions/${positionId}/seekers/${seekerId}/notes/${noteIndex}`;
    await this.httpClient.delete(path);
  }

  async saveOfferMadeNote(
    positionId: string,
    seekerId: string,
    offerDetails: string,
    salary: string | null,
    salaryUnit: string | null,
    customSalaryUnit?: string | null,
    notifyOfferMade?: boolean,
    requisitionId?: string,
    updatedPositionId?: string,
    updatedBusinessId?: string,
    autoDismiss?: boolean,
    offerConditions?: OfferConditionsType,
    employmentStartDate?: Date,
  ): Promise<void> {
    const path = `/api/v1/positions/${positionId}/seekers/${seekerId}/offer`;
    const body = {
      offerDetails,
      notifyOfferMade,
      employmentStartDate,
      offeredSalary: !salary
        ? // istanbul ignore next
          null
        : {
            salary,
            salaryUnit,
            customSalaryUnit,
          },
      requisitionId,
      updatedPositionId,
      updatedBusinessId,
      autoDismiss,
      offerConditions,
    };
    await this.httpClient.post(path, body);
  }

  async addSeekerToFavorite(accountId: string, userId: string, seekerId: string): Promise<void> {
    const path = `/api/v1/account/${accountId}/user/${userId}/favourites`;
    const body = { seekerId };
    await this.httpClient.post(path, body);
  }

  async removeSeekerFromFavorite(accountId: string, userId: string, seekerId: string): Promise<void> {
    const path = `/api/v1/account/${accountId}/user/${userId}/favourites`;
    const body = { data: { seekerId } };
    await this.httpClient.delete(path, body);
  }

  async saveOfferRejectedNote(positionId: string, seekerId: string, offerRejectedDetails: string | null): Promise<void> {
    const path = `/api/v1/positions/${positionId}/seekers/${seekerId}/offer`;
    const body = { data: { offerRejectedDetails } };
    await this.httpClient.delete(path, body);
  }

  async getWebApplyJobListURL(accountId: string, userId: string): Promise<WebApplyUrl> {
    const path = `/api/v1/account/${accountId}/user/${userId}/webApplyUrl`;
    const response = await this.httpClient.get<WebApplyUrl>(path);
    return response.data;
  }

  uploadLogo(accountId: string, companyLogo: string, isSquare: boolean) {
    const path = `/api/v1/account/${accountId}/companyLogo`;
    const body = { companyLogo, isSquare };
    return this.httpClient.post<UploadLogoResponse>(path, body);
  }

  async getCompanyLogo(accountId: string, isSquare: boolean): Promise<string> {
    const path = `/publicApi/v1/account/${accountId}/companyLogo?${new Date().getTime()}&square=${isSquare}`;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const { data, headers } = await this.httpClient.get<ArrayBuffer>(path, {
      responseType: 'arraybuffer',
    });
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (headers['content-type'] === 'application/json; charset=utf-8') {
      return '';
    }
    return `data:image/png;base64,${Buffer.from(data).toString('base64')}`;
  }

  async saveCertnSecrets(accountId: string, apiKey: string, webhookSignature: string): Promise<CertnSecretsResponse> {
    const path = `/api/v1/account/${accountId}/certn`;
    const body = { apiKey, webhookSignature };
    const response = await this.httpClient.post<SuccessMessageResponse>(path, body);
    return response.data;
  }

  async getCertnSecrets(accountId: string): Promise<CertnFormData> {
    const path = `/api/v1/account/${accountId}/certn`;
    const response = await this.httpClient.get<CertnFormData>(path);
    return response.data;
  }

  async administrationBackgroundCheck(accountId: string, backgroundCheckList: BackgroundChecks): Promise<SuccessMessageResponse> {
    const path = `/api/v1/account/${accountId}/certn/screening/options`;
    const response = await this.httpClient.post<SuccessMessageResponse>(path, backgroundCheckList);
    return response.data;
  }

  async getWorkBrightCredentials(accountId: string): Promise<I9FormData> {
    const path = `/api/v1/account/${accountId}/workbright`;
    const response = await this.httpClient.get<I9FormData>(path);
    return response.data;
  }

  async saveWorkBrightCredentials(
    accountId: string,
    workBrightPayload: {
      apiKey: string;
      subDomain: string;
      username: string;
      password: string;
    },
  ): Promise<AxiosResponse> {
    const path = `/api/v1/account/${accountId}/workbright`;
    const response = await this.httpClient.post<SuccessMessageResponse>(path, workBrightPayload);
    return response;
  }

  async getHireFormAPIKey(accountId: string, isRenew: boolean): Promise<string> {
    const path = `/api/v1/hireDetail/apiKey/${accountId}/?isRenew=${isRenew}`;
    const response = await this.httpClient.get<string>(path);
    return response.data;
  }

  async updateModernHireConfigData(accountId: string, configData: ModernHireConfigData): Promise<ModernHireUpdateResponseType> {
    const path = `api/v1/account/${accountId}/modernHire`;
    const response = await this.httpClient.post<ModernHireUpdateResponseType>(path, configData);
    return response.data;
  }

  async getModernHireConfigData(accountId: string): Promise<ModernHireConfigData> {
    const path = `api/v1/account/${accountId}/modernHire`;
    const response = await this.httpClient.get<ModernHireConfigData>(path);
    return response.data;
  }

  async updateHireWebHookUrlInAccount(accountId: string, link: string): Promise<AxiosResponse> {
    const path = `api/v1/account/${accountId}/updateWebhookOnHireDetails/`;
    const response = await this.httpClient.post<ModernHireConfigData>(path, { hireWebhookForCustomer: { url: link } });
    return response;
  }

  async getAllFollowedPositionOfAccount(accountId: string): Promise<string[]> {
    const path = `api/v1/account/${accountId}/followed/positions`;
    const response = await this.httpClient.get<string[]>(path);
    return response.data;
  }

  async getOrganizationHireFormSettings(accountId: string) {
    const path = `/api/v1/account/${accountId}/organization-settings`;
    return this.httpClient.get<TemplateSettingResponse>(path);
  }

  async postOrganizationHireFormSettings(accountId: string, payload: any) {
    const path = `/api/v1/account/${accountId}/organization-settings`;
    return this.httpClient.post(path, payload);
  }

  async switchAccount(accountInviteId: string, userId: string) {
    const path = `/api/v1/user/${userId}/switch`;
    const body = { accountInviteId };
    return this.httpClient.post(path, body);
  }

  async checkUserExists(userId: string): Promise<AxiosResponse> {
    const path = `/api/v1/user/${userId}/exists`;
    return this.httpClient.get(path);
  }

  async getQuestionTemplateList(
    accountId: string,
    params: {
      sortField?: SortField;
      sortDirection?: SortDirection;
      type?: string;
      after?: string;
      before?: string;
      limit?: number;
      searchQuery?: string;
      page?: number;
    },
  ): Promise<AxiosResponse<QuestionTemplatePagination>> {
    const path = `/api/v1/account/${accountId}/question-templates`;
    return this.httpClient.get(path, {
      params,
    });
  }

  async getQuestionTemplateById(accountId: string, templateId: string): Promise<AxiosResponse<QuestionnaireTemplateResponse>> {
    const path = `/api/v1/account/${accountId}/question-templates/${templateId}`;
    return this.httpClient.get(path);
  }
}
